import type { AltTextValue } from '../fields/widgets/alt-text-field';

export const MAX_SLIDES = 10;

export type AuthoredSlideImage = {
  src: string;
  mobileSrc: string;
  alt: AltTextValue;
};

/**
 * Image rows predate connected slide kinds. Their missing discriminator is part of the persisted
 * contract: ordinary manager writes must never add one or rebuild the row from known keys.
 */
export type LegacyImageSlide = AuthoredSlideImage & {
  kind?: undefined;
  linkHref: string;
};

export type TypedImageSlide = AuthoredSlideImage & {
  kind: 'image';
  linkHref: string;
};

export type ImageSlide = LegacyImageSlide | TypedImageSlide;

/** Live Special details are resolved at render time; only identity and authored imagery persist. */
export type SpecialSlide = AuthoredSlideImage & {
  kind: 'special';
  specialId: string;
};

/**
 * A future writer may add another typed row before this renderer understands it. Keep the complete
 * object through author mutations, but fail closed rather than interpreting its fields as an image.
 */
export type UnknownSlide = {
  [key: string]: unknown;
  kind: string;
};

export type Slide = ImageSlide | SpecialSlide | UnknownSlide;

export type SlideRowType = 'image' | 'malformed' | 'special' | 'unknown';

export function isSlideRecord(slide: unknown): slide is Record<string, unknown> {
  return slide !== null && typeof slide === 'object' && !Array.isArray(slide);
}

export function slideRowType(slide: unknown): SlideRowType {
  if (!isSlideRecord(slide)) return 'malformed';
  if (slide.kind === undefined || slide.kind === 'image') return 'image';
  if (slide.kind === 'special') return 'special';
  return 'unknown';
}

/** Runtime Puck data is unvalidated. This returns a safe display value without mutating the row. */
export function slideString(slide: unknown, key: string): string {
  if (!isSlideRecord(slide)) return '';
  return typeof slide[key] === 'string' ? slide[key] : '';
}
